/**
 * Nickname content filter for the public leaderboard.
 *
 * The leaderboard displays player-chosen nicknames to everyone, so under Apple
 * App Store Review Guideline 1.2 (user-generated content) we must filter
 * objectionable names. This is a deliberately conservative blocklist: it
 * catches well-known slurs and strong profanity — including common leetspeak
 * spellings — while trying to avoid flagging innocent names (the classic
 * "Scunthorpe problem": "assassin", "Hancock", "class", "Nigeria" must pass).
 *
 * Two tiers:
 *   SEVERE  — blocked if the term appears ANYWHERE in the normalized name.
 *             Only terms whose substring almost never occurs in an innocent
 *             16-char nickname belong here.
 *   EXACT   — blocked only when the whole normalized name IS the term. Used
 *             for short words that are common substrings of clean words.
 *
 * Keep this file in sync with packages/infra/lambda/profanity.ts (the
 * authoritative server-side copy). They are intentionally identical.
 */

const LEET: Record<string, string> = {
  '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's',
  '7': 't', '8': 'b', '9': 'g', '@': 'a', '$': 's',
  '!': 'i', '|': 'i', '+': 't',
};

/** Fold leetspeak, drop everything but a–z, collapse 3+ repeated letters. */
function normalize(raw: unknown): string {
  let s = String(raw ?? '').toLowerCase();
  s = s.replace(/[01345789@$!|+]/g, (c) => LEET[c] ?? c); // fold leetspeak to letters
  s = s.replace(/[^a-z]/g, ''); // drop spaces, remaining digits, punctuation
  s = s.replace(/(.)\1{2,}/g, '$1'); // collapse 3+ repeats: "fuuuck" -> "fuck"
  return s;
}

// Substring matches. Chosen to minimize innocent collisions.
const SEVERE: readonly string[] = [
  'nigger', 'nigga', 'niglet', 'faggot', 'retard', 'chink', 'kike', 'gook',
  'wetback', 'beaner', 'towelhead', 'cunt', 'nazi', 'hitler', 'fuck',
  'motherfuck', 'shit', 'bitch', 'asshole', 'dumbass', 'jackass', 'dipshit',
  'bullshit', 'cocksuck', 'dickhead', 'pussy', 'whore', 'slut', 'bastard',
  'rapist', 'raping', 'pedophile', 'pedophil', 'molest', 'cumshot', 'blowjob',
  'handjob', 'tittie', 'titties',
];

// Whole-name-only matches (common substrings of clean words).
const EXACT: readonly string[] = [
  'ass', 'fag', 'dick', 'cock', 'coon', 'spic', 'dyke', 'tranny', 'hoe',
  'twat', 'wank', 'jizz', 'cum', 'prick', 'knob', 'bollocks', 'fuk', 'fuc',
  'sht', 'biatch', 'phuck', 'pron', 'porn', 'sex', 'anal', 'nazis',
];

/** True if the nickname is acceptable to display on the public leaderboard. */
export function isNameAllowed(raw: unknown): boolean {
  const n = normalize(raw);
  if (!n) return true; // symbol/space-only resolves to a safe default elsewhere
  for (const t of SEVERE) if (n.includes(t)) return false;
  for (const t of EXACT) if (n === t) return false;
  return true;
}
